import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { APIError, queryUtils, useCreatePdpMutation, useSearchPdpsQuery } from '@vizit/api';
import type { Region, Retailer } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import type { ScorePDPFormSchema } from '@vizit/organisms';
import * as React from 'react';

enum ProductImportErrorType {
  None = 'none',
  Duplicate = 'duplicate',
  UnsupportedCategory = 'unsupported-category',
  Generic = 'generic',
}

const UNSUPPORTED_CATEGORY_ERROR_CODES = ['CATEGORY_NOT_IN_VIZIT_DB', 'CATEGORY_NOT_IN_ORG_ICP'];

interface ParsedAPIError {
  error_code?: string;
}

const parseAPIErrorBody = (error: APIError): ParsedAPIError => {
  try {
    const parsed: unknown = JSON.parse(error.message);
    if (parsed && typeof parsed === 'object' && 'error_code' in parsed) {
      return { error_code: String(parsed.error_code) };
    }
    return {};
  } catch {
    return {};
  }
};

interface UseProductImportDialogProps {
  portfolioId?: string | null;
  retailer?: Retailer;
  region?: Region;
  onSuccessfulImport: (pdpId: string) => void;
  onImportError?: (error: unknown | APIError) => void;
}

interface UseProductImportDialogReturn {
  errorType: ProductImportErrorType;
  duplicatePdpId: string | null;
  onSubmit: (data: ScorePDPFormSchema) => Promise<void>;
  resetState: () => void;
}

const createImportParams = (portfolioId: string, itemId: string, retailer: Retailer, region: Region) => ({
  portfolio_id: portfolioId,
  item_id: itemId,
  region,
  retailer,
});

const findExistingPdp = async (portfolioId: string, itemId: string): Promise<string | null> => {
  try {
    const result = await useSearchPdpsQuery.fetcher({
      portfolio_id: portfolioId,
      search_item_id: itemId,
      limit: 1,
      offset: 0,
    })();

    return result.data?.[0]?.id ?? null;
  } catch {
    return null;
  }
};

const logImportError = (
  error: unknown,
  portfolioId: string | null | undefined,
  itemId: string,
  retailer: Retailer,
  region: Region,
) => {
  const params = createImportParams(portfolioId ?? '', itemId, retailer, region);

  Sentry.addBreadcrumb({
    message: 'Product import failed.',
    level: 'error',
    data: params,
  });

  Sentry.captureException(error, {
    level: 'error',
    tags: {
      feature: SENTRY_FEATURE_TAGS.Product,
      stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Product].productImport,
    },
    extra: {
      portfolio_id: portfolioId,
      request_params: params,
    },
  });
};

const useProductImportDialog = (props: UseProductImportDialogProps): UseProductImportDialogReturn => {
  const { portfolioId, retailer, region, onSuccessfulImport, onImportError } = props;

  const queryClient = useQueryClient();
  const [errorType, setErrorType] = React.useState<ProductImportErrorType>(ProductImportErrorType.None);
  const [duplicatePdpId, setDuplicatePdpId] = React.useState<string | null>(null);

  const { mutateAsync } = useCreatePdpMutation();

  const resetState = React.useCallback(() => {
    setErrorType(ProductImportErrorType.None);
    setDuplicatePdpId(null);
  }, []);

  const handleOnSubmit = React.useCallback(
    async ({ product }: ScorePDPFormSchema) => {
      setErrorType(ProductImportErrorType.None);
      setDuplicatePdpId(null);

      if (!retailer || !region) {
        const configError = new Error('Missing retailer/region configuration');
        setErrorType(ProductImportErrorType.Generic);
        onImportError?.(configError);
        return;
      }

      const params = createImportParams(portfolioId ?? '', product, retailer, region);

      Sentry.addBreadcrumb({
        message: 'Starting product import.',
        level: 'info',
        data: params,
      });

      try {
        const responseData = await mutateAsync(params);

        if (responseData?.id) {
          Sentry.addBreadcrumb({
            message: 'Product imported successfully.',
            level: 'info',
            data: { pdp_id: responseData.id, ...params },
          });

          // Invalidate PDP list queries so newly imported PDP appears in search results
          await queryUtils.invalidateAllPdpLists(queryClient, portfolioId ?? undefined);

          onSuccessfulImport(responseData.id);
        } else {
          throw new Error(`Failed to create PDP ID: ${JSON.stringify(params)}`);
        }
      } catch (error) {
        if (error instanceof APIError && error.status === 409) {
          setErrorType(ProductImportErrorType.Duplicate);
          setDuplicatePdpId(portfolioId ? await findExistingPdp(portfolioId, product) : null);

          Sentry.addBreadcrumb({
            message: 'Duplicate product import attempted.',
            level: 'info',
            data: params,
          });
        } else {
          const errorCode = error instanceof APIError ? parseAPIErrorBody(error).error_code : undefined;

          if (errorCode && UNSUPPORTED_CATEGORY_ERROR_CODES.includes(errorCode)) {
            setErrorType(ProductImportErrorType.UnsupportedCategory);
          } else {
            setErrorType(ProductImportErrorType.Generic);
          }
          logImportError(error, portfolioId, product, retailer, region);
        }

        onImportError?.(error);
      }
    },
    [portfolioId, retailer, region, mutateAsync, queryClient, onSuccessfulImport, onImportError],
  );

  return {
    errorType,
    duplicatePdpId,
    onSubmit: handleOnSubmit,
    resetState,
  };
};

export { useProductImportDialog, ProductImportErrorType };
export type { UseProductImportDialogProps };
